import { useQuery } from '@tanstack/react-query';
import { T, Var, useGT } from 'gt-react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArchiveIcon,
  ArrowCounterClockwiseIcon,
  ArrowsClockwiseIcon,
  ChatTextIcon,
  CircleIcon,
  ClockCounterClockwiseIcon,
  FolderPlusIcon,
  LinkBreakIcon,
  NotePencilIcon,
  PaperclipIcon,
  PencilSimpleIcon,
  TrashIcon,
  UserSwitchIcon,
  type Icon,
} from '@phosphor-icons/react';
import type {
  PricelistCaseLegacyEventDTO,
  PricelistCaseResponse,
  PricelistCaseTimelineEntryDTO,
} from '@erp/rpc';
import { orpc } from '@/api/rpc/client';
import { pricelistVendorName } from '@/components/company/companyPickerDisplay';
import { SectionLabel } from './pricelistDetailFields';
import { pricelistEventLabel } from './pricelistEventLabels';
import {
  isPricelistCaseVendorAuditField,
  useCaseAuditFieldLabel,
  usePricelistCaseWaitingOnLabel,
} from './pricelistCaseLabels';
import {
  formatPricelistCaseAuditValue,
  formatPricelistCaseVendorNumbersAuditValue,
} from './pricelistCaseFilesReady';
import {
  formatPricelistCaseVendorAuditValue,
  pricelistCaseVendorNameById,
} from './pricelistCaseVendors';
import {
  classifyPricelistCaseFileAction,
  payloadDocumentId,
  payloadNoteValue,
} from './pricelistCaseFileHistory';
import {
  classifyPricelistCaseCommentAction,
  payloadCommentText,
} from './pricelistCaseCommentHistory';

/**
 * A pricelist update case's history.
 *
 * Purpose-built rather than the generic `TaskTimeline`: that component is the
 * TASK's front door (it links to `/tasks/$taskId`, offers run inspection and a
 * view switch), and the design is explicit that the case page is the only
 * front door for this type. What a case actually produces is a short list of
 * kinds — it opened, its owner or state changed, a file was attached or
 * detached, a domain field moved — so this renders those and falls back to a
 * plain line for anything else rather than dragging the task surface in.
 *
 * Three sources, merged server-side and rendered in one list, with the legacy
 * pre-case events beneath and labelled: `audit_change_log` on the case (field
 * diffs), `core_task_actions` + `audit_change_log` on the companion task, and
 * the untouched `im_pricelist_events` rows of the attached files.
 */

interface Props {
  pricelistCase: PricelistCaseResponse;
  /** Display names for the actors, keyed by user id. */
  userNameById: Map<string, string>;
}

function Timestamp({ at }: { at: Date }) {
  return (
    <time
      className="shrink-0 tabular-nums text-xs text-muted-foreground"
      dateTime={at.toISOString()}
      title={format(at, 'MMM d, yyyy · h:mm a')}
    >
      {formatDistanceToNow(at, { addSuffix: true })}
    </time>
  );
}

function useCaseDiffValue(
  vendorNameById: ReadonlyMap<string, string>,
): (field: string, value: unknown, emptyLabel: string) => string {
  const gt = useGT();
  const waitingOnLabel = usePricelistCaseWaitingOnLabel();
  const allVendorNumbers = gt('All vendor #s', {
    context:
      'timeline value when a price change covers every vendor number of its vendors (no specific vendor numbers picked)',
  });
  return (field, value, emptyLabel) => {
    if (field === 'vendor_numbers') {
      return formatPricelistCaseVendorNumbersAuditValue(
        value,
        emptyLabel,
        allVendorNumbers,
      );
    }
    if (isPricelistCaseVendorAuditField(field)) {
      return (
        formatPricelistCaseVendorAuditValue(
          value,
          vendorNameById,
          pricelistVendorName,
          emptyLabel,
        ) ??
        formatPricelistCaseAuditValue(field, value, emptyLabel, waitingOnLabel)
      );
    }
    return formatPricelistCaseAuditValue(
      field,
      value,
      emptyLabel,
      waitingOnLabel,
    );
  };
}

/**
 * One icon per activity kind, so the list scans by shape before it is read.
 * Keyed by name so each row can mark which icon it shows (`data-activity-icon`).
 */
const ACTIVITY_ICONS = {
  FolderPlus: FolderPlusIcon,
  ArrowsClockwise: ArrowsClockwiseIcon,
  UserSwitch: UserSwitchIcon,
  PencilSimple: PencilSimpleIcon,
  Paperclip: PaperclipIcon,
  LinkBreak: LinkBreakIcon,
  Archive: ArchiveIcon,
  ArrowCounterClockwise: ArrowCounterClockwiseIcon,
  NotePencil: NotePencilIcon,
  ChatText: ChatTextIcon,
  Trash: TrashIcon,
  Circle: CircleIcon,
  ClockCounterClockwise: ClockCounterClockwiseIcon,
} as const satisfies Record<string, Icon>;

type ActivityIconName = keyof typeof ACTIVITY_ICONS;

function EntryRow({
  children,
  at,
  icon,
}: {
  children: React.ReactNode;
  at: Date;
  icon: ActivityIconName;
}) {
  const IconComponent = ACTIVITY_ICONS[icon];
  return (
    <li className="flex items-baseline justify-between gap-3 border-b py-1.5 last:border-b-0">
      <span className="flex min-w-0 items-start gap-2">
        {/* Decorative: the row text stays the accessible label. A line-high
            box centres the icon on the first line of text, however it wraps. */}
        <span
          aria-hidden
          data-activity-icon={icon}
          className="flex h-5 shrink-0 items-center text-muted-foreground"
        >
          <IconComponent className="size-3.5" />
        </span>
        <span className="min-w-0 text-sm text-foreground">{children}</span>
      </span>
      <Timestamp at={at} />
    </li>
  );
}

/** The actor's name, or a readable stand-in. */
function useActorName(
  userNameById: Map<string, string>,
): (userId: string | null) => string {
  const gt = useGT();
  return (userId) => {
    if (userId === null) {
      return gt('System', {
        context: 'timeline actor for a change with no acting user',
      });
    }
    return (
      userNameById.get(userId) ??
      gt('Someone', {
        context: 'timeline actor when the acting user cannot be named',
      })
    );
  };
}

function FileChip({ fileName }: { fileName: string }) {
  return (
    <span className="rounded bg-muted px-1 py-0.5 text-xs">{fileName}</span>
  );
}

function CommentHistoryEntry({
  kind,
  timestamp,
  payload,
  actorName,
  emptyLabel,
}: {
  kind: NonNullable<ReturnType<typeof classifyPricelistCaseCommentAction>>;
  timestamp: Date;
  payload: unknown;
  actorName: string;
  emptyLabel: string;
}) {
  const at = new Date(timestamp);
  const previous = payloadCommentText(payload, 'previousBody') ?? emptyLabel;
  const next = payloadCommentText(payload, 'body') ?? emptyLabel;

  if (kind === 'added') {
    return (
      <EntryRow icon="ChatText" at={at}>
        <T context="pricelist case timeline entry for a discussion note being added">
          <Var>{actorName}</Var> added a note: <Var>{next}</Var>
        </T>
      </EntryRow>
    );
  }
  if (kind === 'deleted') {
    return (
      <EntryRow icon="Trash" at={at}>
        <T context="pricelist case timeline entry for a discussion note being deleted">
          <Var>{actorName}</Var> deleted a note: <Var>{previous}</Var>
        </T>
      </EntryRow>
    );
  }
  return (
    <EntryRow icon="PencilSimple" at={at}>
      <T context="pricelist case timeline entry for a discussion note being edited">
        <Var>{actorName}</Var> edited a note: <Var>{previous}</Var> →{' '}
        <Var>{next}</Var>
      </T>
    </EntryRow>
  );
}

function FileHistoryEntry({
  kind,
  timestamp,
  payload,
  fileName,
  actorName,
  emptyLabel,
}: {
  kind: NonNullable<ReturnType<typeof classifyPricelistCaseFileAction>>;
  timestamp: Date;
  payload: unknown;
  fileName: string;
  actorName: string;
  emptyLabel: string;
}) {
  const at = new Date(timestamp);

  if (kind === 'attached') {
    return (
      <EntryRow icon="Paperclip" at={at}>
        <T context="pricelist case timeline entry for a file attached to the case">
          <Var>{actorName}</Var> attached{' '}
          <Var>
            <FileChip fileName={fileName} />
          </Var>
        </T>
      </EntryRow>
    );
  }
  if (kind === 'detached') {
    return (
      <EntryRow icon="LinkBreak" at={at}>
        <T context="pricelist case timeline entry for a file removed from the case">
          <Var>{actorName}</Var> detached{' '}
          <Var>
            <FileChip fileName={fileName} />
          </Var>
        </T>
      </EntryRow>
    );
  }
  if (kind === 'deprecated') {
    return (
      <EntryRow icon="Archive" at={at}>
        <T context="pricelist case timeline entry for a file marked deprecated">
          <Var>{actorName}</Var> deprecated{' '}
          <Var>
            <FileChip fileName={fileName} />
          </Var>
        </T>
      </EntryRow>
    );
  }
  if (kind === 'reinstated') {
    return (
      <EntryRow icon="ArrowCounterClockwise" at={at}>
        <T context="pricelist case timeline entry for a deprecated file being reinstated">
          <Var>{actorName}</Var> reinstated{' '}
          <Var>
            <FileChip fileName={fileName} />
          </Var>
        </T>
      </EntryRow>
    );
  }
  return (
    <EntryRow icon="NotePencil" at={at}>
      <T context="pricelist case timeline entry for a changed file note">
        <Var>{actorName}</Var> changed the note on{' '}
        <Var>
          <FileChip fileName={fileName} />
        </Var>
        : <Var>{payloadNoteValue(payload, 'previousNotes') ?? emptyLabel}</Var>{' '}
        → <Var>{payloadNoteValue(payload, 'notes') ?? emptyLabel}</Var>
      </T>
    </EntryRow>
  );
}

function HumanActionEntry({
  entry,
  fileNameByDocumentId,
  actorName,
  emptyLabel,
}: {
  entry: Extract<PricelistCaseTimelineEntryDTO, { kind: 'human_action' }>;
  fileNameByDocumentId: Map<string, string>;
  actorName: (userId: string | null) => string;
  emptyLabel: string;
}) {
  const gt = useGT();
  const actorUserId = entry.actor.type === 'user' ? entry.actor.userId : null;
  // File-scoped actions carry the document id in their payload, which is
  // exactly what lets them render with a file chip instead of a bare key.
  const documentId = payloadDocumentId(entry.payload);
  const fileName = documentId
    ? (fileNameByDocumentId.get(documentId) ??
      gt('a file', {
        context:
          'pricelist case timeline fallback when the named file is no longer on the case',
      }))
    : null;
  const fileHistoryKind = classifyPricelistCaseFileAction(entry.actionKey);
  if (fileHistoryKind !== null && fileName !== null) {
    return (
      <FileHistoryEntry
        kind={fileHistoryKind}
        timestamp={entry.timestamp}
        payload={entry.payload}
        fileName={fileName}
        actorName={actorName(actorUserId)}
        emptyLabel={emptyLabel}
      />
    );
  }
  const commentHistoryKind = classifyPricelistCaseCommentAction(
    entry.actionKey,
  );
  if (commentHistoryKind !== null) {
    return (
      <CommentHistoryEntry
        kind={commentHistoryKind}
        timestamp={entry.timestamp}
        payload={entry.payload}
        actorName={actorName(actorUserId)}
        emptyLabel={emptyLabel}
      />
    );
  }
  return (
    <EntryRow icon="Circle" at={new Date(entry.timestamp)}>
      <T context="pricelist case timeline entry for an action with no specific rendering">
        <Var>{actorName(actorUserId)}</Var>: <Var>{entry.actionKey}</Var>
      </T>
    </EntryRow>
  );
}

function TimelineEntry({
  entry,
  fileNameByDocumentId,
  userNameById,
  vendorNameById,
}: {
  entry: PricelistCaseTimelineEntryDTO;
  fileNameByDocumentId: Map<string, string>;
  userNameById: Map<string, string>;
  vendorNameById: ReadonlyMap<string, string>;
}) {
  const gt = useGT();
  const fieldLabel = useCaseAuditFieldLabel();
  const fieldValue = useCaseDiffValue(vendorNameById);
  const actorName = useActorName(userNameById);
  const emptyLabel = gt('nothing', {
    context: 'timeline placeholder for a field value that was not set',
  });

  if (entry.kind === 'case_field_changed') {
    return (
      <EntryRow icon="PencilSimple" at={new Date(entry.timestamp)}>
        <T context="pricelist case timeline entry for a changed domain field">
          <Var>{fieldLabel(entry.field)}</Var>:{' '}
          <Var>{fieldValue(entry.field, entry.previousValue, emptyLabel)}</Var>{' '}
          → <Var>{fieldValue(entry.field, entry.value, emptyLabel)}</Var> by{' '}
          <Var>{actorName(entry.actorUserId)}</Var>
        </T>
      </EntryRow>
    );
  }

  const actorUserId = entry.actor.type === 'user' ? entry.actor.userId : null;

  if (entry.kind === 'human_action') {
    return (
      <HumanActionEntry
        entry={entry}
        fileNameByDocumentId={fileNameByDocumentId}
        actorName={actorName}
        emptyLabel={emptyLabel}
      />
    );
  }

  if (entry.kind === 'task_created') {
    return (
      <EntryRow icon="FolderPlus" at={new Date(entry.timestamp)}>
        <T context="pricelist case timeline entry for the case being opened">
          <Var>{actorName(actorUserId)}</Var> opened the price change
        </T>
      </EntryRow>
    );
  }

  if (entry.kind === 'task_status_changed') {
    return (
      <EntryRow icon="ArrowsClockwise" at={new Date(entry.timestamp)}>
        <T context="pricelist case timeline entry for a status change">
          Status: <Var>{entry.previousStatus}</Var> → <Var>{entry.status}</Var>{' '}
          by <Var>{actorName(actorUserId)}</Var>
        </T>
      </EntryRow>
    );
  }

  if (entry.kind === 'task_assignee_changed') {
    return (
      <EntryRow icon="UserSwitch" at={new Date(entry.timestamp)}>
        <T context="pricelist case timeline entry for an assignee change">
          Assigned to{' '}
          <Var>
            {entry.assigneeUserId
              ? actorName(entry.assigneeUserId)
              : gt('nobody', {
                  context:
                    'pricelist case timeline value when a case is unassigned',
                })}
          </Var>{' '}
          by <Var>{actorName(actorUserId)}</Var>
        </T>
      </EntryRow>
    );
  }

  // Everything else a task can record (agent runs, workflow steps) is not part
  // of this type's lifecycle yet. Rendered as its kind rather than dropped, so
  // an unexpected fact is visible instead of invisible.
  return (
    <EntryRow icon="Circle" at={new Date(entry.timestamp)}>
      <span className="text-muted-foreground">{entry.kind}</span>
    </EntryRow>
  );
}

/** The attached files' own pre-case history, read-only and clearly marked. */
function LegacyEvents({
  events,
  fileNameByDocumentId,
}: {
  events: PricelistCaseLegacyEventDTO[];
  fileNameByDocumentId: Map<string, string>;
}) {
  const gt = useGT();
  if (events.length === 0) return null;
  return (
    <div className="mt-3 flex flex-col gap-1 border-t pt-3">
      <span className="text-xs font-medium text-muted-foreground">
        <T context="heading for a pricelist case's pre-case file history">
          Before this price change existed
        </T>
      </span>
      <p className="text-xs text-muted-foreground">
        <T context="explanation that pre-case history belongs to the files, not the case">
          Recorded against the files themselves, before they were pulled into a
          price change. Read-only.
        </T>
      </p>
      <ul className="mt-1 flex flex-col">
        {events.map((event) => (
          <EntryRow
            icon="ClockCounterClockwise"
            key={event.id}
            at={new Date(event.createdAt)}
          >
            <span className="text-muted-foreground">
              <span className="mr-1 rounded bg-muted px-1 py-0.5 text-xs">
                {fileNameByDocumentId.get(event.documentId) ??
                  gt('a file', {
                    context:
                      'pricelist case legacy history fallback for an unnamed file',
                  })}
              </span>
              {pricelistEventLabel(gt, event)}
            </span>
          </EntryRow>
        ))}
      </ul>
    </div>
  );
}

export function PricelistCaseTimeline({ pricelistCase, userNameById }: Props) {
  const gt = useGT();
  const timelineQuery = useQuery(
    orpc.inventoryManagement.pricelistCases.timeline.queryOptions({
      input: { caseId: pricelistCase.id },
    }),
  );
  const timeline = timelineQuery.data;

  const fileNameByDocumentId = new Map(
    pricelistCase.files.map((file) => [file.documentId, file.name]),
  );
  const vendorNameById = pricelistCaseVendorNameById(
    pricelistCase.vendors,
    timeline?.vendorNameByCompanyId ?? {},
  );

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-card px-4 py-3">
      <SectionLabel>
        <T context="heading for a pricelist update case's history">History</T>
      </SectionLabel>
      {timelineQuery.isPending ? (
        <p className="text-sm text-muted-foreground">
          <T context="loading state for a pricelist case's history">
            Loading history…
          </T>
        </p>
      ) : timeline === undefined ? (
        <p className="text-sm text-destructive">
          <T context="error state when a pricelist case's history cannot be loaded">
            We couldn't load this price change's history.
          </T>
        </p>
      ) : (
        <>
          {timeline.entries.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              <T context="empty state for a pricelist case's history">
                Nothing has happened on this price change yet.
              </T>
            </p>
          ) : (
            <ul className="flex flex-col">
              {timeline.entries.map((entry) => (
                <TimelineEntry
                  key={entry.id}
                  entry={entry}
                  fileNameByDocumentId={fileNameByDocumentId}
                  userNameById={userNameById}
                  vendorNameById={vendorNameById}
                />
              ))}
            </ul>
          )}
          {timeline.truncated && (
            <p className="text-xs text-muted-foreground">
              {gt('Only the most recent entries are shown.', {
                context:
                  "note when a pricelist case's history is longer than the page",
              })}
            </p>
          )}
          <LegacyEvents
            events={timeline.legacyEvents}
            fileNameByDocumentId={fileNameByDocumentId}
          />
        </>
      )}
    </div>
  );
}
